import type {
	InferPrimitiveType,
	PrimitiveTypeByName,
	PrimitiveTypeName,
} from '#layers/data-model/types/schema/primitive-type';
import { Temporal } from 'temporal-polyfill';
import { isZonedDateTime } from '#layers/base/public';
import { PRIMITIVE_TYPE_DATE, PRIMITIVE_TYPE_DATE_TIME, PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE, PRIMITIVE_TYPE_TIME } from '#layers/data-model/constants/schema/primitive-type';

interface FieldParserInfo<T = any> {
	parse?: (value: unknown) => T | null;
	serialize?: (value: T) => unknown;
}

type FieldParserMap = Partial<{
	[K in PrimitiveTypeName]: FieldParserInfo<InferPrimitiveType<PrimitiveTypeByName<K>>>
}>;

const FIELD_PARSERS: FieldParserMap = {
	[PRIMITIVE_TYPE_DATE]: {
		parse: (value) => {
			if (value == null) return null;
			if (value instanceof Temporal.PlainDate) return value;
			if (typeof value !== 'string') return null;

			try {
				return Temporal.PlainDate.from(value);
			}
			catch {
				return null;
			}
		},
		serialize: (value) => value == null ? null : value.toString(),
	},
	[PRIMITIVE_TYPE_TIME]: {
		parse: (value) => {
			if (value == null) return null;
			if (value instanceof Temporal.PlainTime) return value;
			if (typeof value !== 'string') return null;

			try {
				return Temporal.PlainTime.from(value);
			}
			catch {
				return null;
			}
		},
		serialize: (value) => value == null ? null : value.toString(),
	},
	[PRIMITIVE_TYPE_DATE_TIME]: {
		parse: (value) => {
			if (value == null) return null;
			if (value instanceof Temporal.PlainDateTime) return value;
			if (typeof value !== 'string') return null;

			try {
				return Temporal.PlainDateTime.from(value);
			}
			catch {
				return null;
			}
		},
		serialize: (value) => value == null ? null : value.toString(),
	},
	[PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE]: {
		parse: (value) => {
			if (value == null) return null;
			if (isZonedDateTime(value)) return value;
			if (typeof value !== 'string') return null;

			try {
				const instant = Temporal.Instant.from(value);
				return instant.toZonedDateTimeISO('UTC');
			}
			catch {
				return null;
			}
		},
		serialize: (value) => {
			if (value == null) return null;
			if (typeof value === 'string') return value;

			if (!isZonedDateTime(value)) {
				return String(value);
			}

			const utc = value.withTimeZone('UTC');
			return utc.toString().replace(/\[.*\]$/, ''); // remove the timezone annotation
		},
	},
};

export function resolveFieldParser<TName extends PrimitiveTypeName, TType = InferPrimitiveType<PrimitiveTypeByName<TName>>>(type: TName): { parse?: (value: unknown) => TType; serialize?: (value: TType) => unknown } | undefined {
	return FIELD_PARSERS[type] as { parse?: (value: unknown) => TType; serialize?: (value: TType) => unknown } | undefined;
}
